import { EventEmitter, h, Component, Host, Event, Listen, Prop, Element } from '@stencil/core';

@Component({
  tag: 'pk-tabs',
  styleUrl: './scss/pk-tabs.scss',
  scoped: true,
})
export class PkTabs {
  // Own Properties
  // ====================

  private moreButtonElement: HTMLPkButtonElement;
  private moreDropDownElement: HTMLPkDropdownElement;
  private moreNavigationMenuIcon: HTMLPkIconElement;

  private moreButtonCssSelector: string = '.pk-tabs__more-button';
  private dropDownSelector: string = 'pk-dropdown';
  private iconCloseName: string = 'navigation-close';
  private iconOpenName: string = 'navigation-menu';
  private containerElement: HTMLDivElement;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkTabsElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** If true, a "more" button is displayed in order to see the other tabs that do not fit on the screen */
  @Prop() enableMore: boolean;

  /** Tabs alignment */
  @Prop() alignment: 'left' | 'center' | 'right' | 'stretch' = 'left';

  // Prop lifecycle events
  // =======================

  /** Callback handler to be called when the tab selection change. */
  @Event() pkChange: EventEmitter<{ tabId: string; tabElem: HTMLPkTabElement }>;

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentDidLoad() {
    this.handleMoreSlotChange();

    const selectedTab: HTMLPkTabElement = this.getSelected();
    this.setActivePanel(selectedTab);
  }

  disconnectedCallback() {
    if (this.moreDropDownElement) {
      this.moreDropDownElement.removeEventListener('pkShow', this.handleMoreDropdownOpen);
      this.moreDropDownElement.removeEventListener('pkHide', this.handleMoreDropdownClose);
    }
  }

  // Listeners
  // ==============
  @Listen('pkTabButtonClick')
  onTabButtonClick(ev?: UIEvent) {
    this.setSelectedTab(ev.target as HTMLPkTabElement);

    const selectedTab: HTMLPkTabElement = this.getSelected();
    this.pkChange.emit({ tabId: selectedTab.tabId, tabElem: selectedTab });
  }

  @Listen('resize', { target: 'window' })
  onWindowResize() {
    this.handleMoreButtonOption();
  }

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private getTabs = () => {
    const tabs = this.el.querySelectorAll('pk-tab');
    return Array.from(tabs);
  };

  private getPanels = () => {
    const panels = this.el.querySelectorAll('pk-tab-panel');
    return Array.from(panels);
  };

  private setSelectedTab = (tabElement: HTMLPkTabElement) => {
    this.getTabs().forEach((tab) => (tab.selected = tab === tabElement));
    this.setActivePanel(tabElement);
  };

  private setActivePanel = (selectedTab: HTMLPkTabElement) => {
    this.getPanels().forEach((panel) => (panel.active = panel.tab === selectedTab.tabId));
  };

  private getSelected = () => {
    return this.getTabs().find((i) => i.selected);
  };

  private handleMoreSlotChange = () => {
    this.moreButtonElement = this.el.querySelector(this.moreButtonCssSelector);
    this.moreDropDownElement = this.el.querySelector(this.dropDownSelector);
    this.moreNavigationMenuIcon = this.moreButtonElement.querySelector(`[name="${this.iconOpenName}"]`);

    this.handleMoreButtonOption();

    if (this.moreDropDownElement) {
      this.moreDropDownElement.addEventListener('pkShow', this.handleMoreDropdownOpen);
      this.moreDropDownElement.addEventListener('pkHide', this.handleMoreDropdownClose);
    }
  };

  private handleMoreDropdownOpen = () => {
    if (!this.moreNavigationMenuIcon) return;
    this.moreNavigationMenuIcon.name = this.iconCloseName;
  };

  private handleMoreDropdownClose = () => {
    if (!this.moreNavigationMenuIcon) return;
    this.moreNavigationMenuIcon.name = this.iconOpenName;
  };

  private handleMoreButtonOption = () => {
    if (!this.moreButtonElement || !this.containerElement) return;

    let sum = 0;
    this.getTabs().forEach((tab) => {
      sum = sum + tab.offsetWidth;
    });

    const moreButtonWidth = this.moreButtonElement.clientWidth;
    const displayMoreButton: boolean = sum > this.containerElement.clientWidth - moreButtonWidth && this.enableMore;
    if (displayMoreButton) {
      this.moreButtonElement.classList.add('visible');
      return;
    }

    this.moreButtonElement.classList.remove('visible');
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <Host>
        <div
          class={{
            'pk-tabs__tab-bar': true,
            [`pk-tabs__tab-bar--${this.alignment}`]: true,
          }}
          ref={(el) => (this.containerElement = el)}
          part="tab-bar"
        >
          <div class="pk-tabs__tabs-container" role="tablist" part="tabs">
            <slot />
          </div>
          <div class="pk-tabs__more-button" part="more-button">
            <slot name="more" onSlotchange={this.handleMoreSlotChange} />
          </div>
        </div>
        <div class="pk-tabs__content" part="content">
          <slot name="content" />
        </div>
      </Host>
    );
  }
}
